import { useState } from 'react';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { validateInventorizationReadiness } from '@/features/inventorization/domain/inventorization-validation';
import { CAPTURE_SESSION_STATUSES, type ValidationIssue } from '@/shared/capture/capture-session';

interface QueueInventorizationState {
  error: string | null;
  isQueueing: boolean;
  issues: ValidationIssue[];
}

export function useQueueInventorizationSession(sessionId: string) {
  const { activeLocation } = useStartupSession();
  const repositories = useInventoryAppServices();
  const [state, setState] = useState<QueueInventorizationState>({
    error: null,
    isQueueing: false,
    issues: [],
  });

  async function queueOffline() {
    setState({ error: null, isQueueing: true, issues: [] });

    try {
      const session = await repositories.captureSessions.get(sessionId);

      if (!session) {
        throw new Error(`Draft ${sessionId} was not found.`);
      }

      const issues = validateInventorizationReadiness(session);
      const locationApiKey = session.locationApiKey ?? activeLocation?.apiKey;

      if (!locationApiKey?.trim()) {
        throw new Error('Active location API key is required before queueing this meter.');
      }

      const queuedAt = new Date().toISOString();
      const queuedSession = {
        ...session,
        locationApiKey: locationApiKey.trim(),
        status: CAPTURE_SESSION_STATUSES.queued,
        submittedAt: queuedAt,
        updatedAt: queuedAt,
        validationErrors: issues,
      };

      await repositories.captureSessions.save(queuedSession);
      await repositories.syncQueue.enqueueSession(queuedSession, queuedAt);
      setState({ error: null, isQueueing: false, issues });

      return queuedSession;
    } catch (error) {
      setState({
        error: error instanceof Error ? error.message : 'Unable to queue inventorization draft.',
        isQueueing: false,
        issues: [],
      });

      return undefined;
    }
  }

  return {
    ...state,
    queueOffline,
  };
}
